import { Anchor, Box, Button, Grid, Group, Progress, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { notifications } from "@dehub/theme";
import { ArrowLeftIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { ReturnApi, TrustScoreApi } from "../../../apis";
import { DeliveryActionButton } from "../../../components/delivery/action-controls";
import { KpiTile, TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { DetailCard, SideRow } from "../../../components/return/detail-card";
import { TrustLevelBadge, trustLevelLabel } from "../../../components/return/trust-level-badge";
import { TrustBenchmarkWidget } from "../../../components/return/trust-benchmark-widget";
import { ReturnStatusBadge, reasonLabel } from "../../../components/return/atoms";
import { DeniedCard, ListState } from "../../../components/ui";
import { currencyFormat } from "../../../utils/currency";
import { dateFormat } from "../../../utils/date";
import { message } from "../../../utils/message";

export function TrustScoreDetailPage() {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const { businessId = "" } = useParams<{ businessId: string }>();
  const { hasAccess } = usePermission("RETURN_ANALYTICS_VIEW");
  const [recomputing, setRecomputing] = useState(false);

  const { data, error, isLoading, mutate } = useSWR(
    businessId && hasAccess ? ["trust-scores.get", businessId] : null,
    () => TrustScoreApi.get(businessId),
    { revalidateOnFocus: false }
  );

  // Recent returns for this buyer (last 10).
  const { data: recent } = useSWR(
    businessId && hasAccess ? ["trust-scores.recent", businessId] : null,
    () => ReturnApi.list({ buyerBusinessId: businessId, limit: 10, sortBy: "requestedAt", sortDir: "desc" }),
    { revalidateOnFocus: false }
  );
  const recentRows = recent?.returns ?? [];
  // The trust-score payload has no business reference code; the buyer's returns carry it (BU-…).
  const refCode = recentRows.find((r) => r.buyerBusinessRefCode)?.buyerBusinessRefCode ?? null;

  // O206 — limited to THIS buyer through `businessId`, so the button name matches what it does.
  const onRecompute = useCallback(async () => {
    setRecomputing(true);
    try {
      await TrustScoreApi.recompute(businessId);
      await mutate();
      notifications.show({
        color  : "teal",
        title  : t("trustDetail.successTitle"),
        message: t("trustDetail.recomputeQueued"),
      });
    } catch (e) {
      const msg = (e as Error)?.message;
      if (typeof msg === "string" && msg.startsWith("error.")) message.error(msg);
      else notifications.show({ color: "red", title: t("toast.error"), message: t("trustDetail.recomputeError") });
    } finally {
      setRecomputing(false);
    }
  }, [businessId, mutate, t]);

  const name = data?.businessName || "-";
  const breadcrumb = [
    { to: "/",                       label: t("breadcrumb.home") },
    { to: "/returns",                label: t("breadcrumb.returns") },
    { to: "/returns/trust-scores",   label: t("trustDetail.trustScore") },
    { label: data ? name : t("breadcrumb.detail") },
  ];

  if (!hasAccess) {
    return (
      <PageLayout title={t("trustDetail.trustScore")} breadcrumb={breadcrumb}>
        <DeniedCard permission="RETURN_ANALYTICS_VIEW" />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={data ? name : t("trustDetail.trustScore")}
      subTitle={data && refCode ? `${refCode} · ${t("trustDetail.subtitle")}` : t("trustDetail.subtitle")}
      breadcrumb={breadcrumb}
      loading={isLoading && !data}
      extra={[
        <DeliveryActionButton
          key="recompute"
          state={{ allowed: hasAccess, reason: null }}
          variant="default"
          leftSection={<ArrowsClockwiseIcon size={18} />}
          loading={recomputing}
          onClick={onRecompute}
        >
          {t("trustDetail.recomputeThis")}
        </DeliveryActionButton>,
      ]}
    >
      {error && !data ? (
        <Stack gap="md" align="flex-start">
          <ListState
            state="error"
            onRetry={() => mutate()}
            errorCode={(error as { statusCode?: number })?.statusCode}
          />
          <Button variant="default" leftSection={<ArrowLeftIcon size={18} />} onClick={() => navigate("/returns/trust-scores")}>
            {t("prevention.detail.backToList")}
          </Button>
        </Stack>
      ) : null}

      {data && (
        <Stack gap="md">
          {/* Header card */}
          <DetailCard>
            <Stack gap={6}>
              <Group gap="sm" wrap="wrap">
                <TrustLevelBadge level={data.level} />
                <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)" fw={600}>
                  {t("trustDetail.scoreValue", { score: data.rawScore.toFixed(1) })}
                </Text>
              </Group>
              {data.previousLevel && data.previousLevel !== data.level && (
                <Text size="sm" c="var(--nw-text-2)">
                  {t("trustDetail.previous")}:{" "}
                  {trustLevelLabel(data.previousLevel)}
                  {data.levelChangedAt && ` (${dateFormat(data.levelChangedAt)})`}
                </Text>
              )}
            </Stack>
          </DetailCard>

          {/* KPI tiles */}
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
            <KpiTile label={t("trustDetail.orderCount")} value={data.totalOrders.toLocaleString("mn-MN")} />
            <KpiTile label={t("breadcrumb.returns")} value={data.totalReturns} meta={`${data.returnRatePercent.toFixed(1)}%`} />
            <KpiTile label={t("trustDetail.dispute")} value={data.disputeCount} meta={`${data.disputeRatePercent.toFixed(1)}%`} />
            <KpiTile label={t("trustDetail.totalRefunded")} value={currencyFormat(data.totalRefundedAmount)} />
          </SimpleGrid>

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <DetailCard title={t("trustDetail.timeSection")}>
                <SideRow label={t("trustDetail.windowStart")} value={dateFormat(data.windowStart)} mono />
                <SideRow label={t("trustDetail.windowEnd")} value={dateFormat(data.windowEnd)} mono />
                <SideRow label={t("trustDetail.computedAt")} value={dateFormat(data.computedAt)} mono />
                <SideRow label={t("trustDetail.computationVersion")} value={`v${data.computationVersion}`} mono />
              </DetailCard>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <DetailCard title={t("trustDetail.additional")}>
                <SideRow label={t("trustDetail.highValueReturns")} value={data.highValueReturnsCount} mono />
                <SideRow label={t("trustDetail.rejectedCount")} value={data.rejectedCount} mono />
                <SideRow label={t("trustDetail.cancelledCount")} value={data.cancelledCount} mono />
              </DetailCard>
            </Grid.Col>
          </Grid>

          {/* Components breakdown — the 4 risk dimensions, all real metrics from the trust-score payload. */}
          <DetailCard title={t("trustDetail.componentMetrics")}>
            <Stack gap="md">
              <Box>
                <Group justify="space-between" mb={4}>
                  <Text size="sm" c="var(--nw-text-2)">{t("benchmark.returnRate")}</Text>
                  <Text size="sm" fw={600} ff="monospace" fz="var(--nw-fz-mono)">{data.returnRatePercent.toFixed(1)}%</Text>
                </Group>
                <Progress value={Math.min(100, data.returnRatePercent)} color="gray.7" radius="sm" aria-label={t("benchmark.returnRate")} />
              </Box>
              <Box>
                <Group justify="space-between" mb={4}>
                  <Text size="sm" c="var(--nw-text-2)">{t("benchmark.disputeRate")}</Text>
                  <Text size="sm" fw={600} ff="monospace" fz="var(--nw-fz-mono)">{data.disputeRatePercent.toFixed(1)}%</Text>
                </Group>
                <Progress value={Math.min(100, data.disputeRatePercent)} color="gray.7" radius="sm" aria-label={t("benchmark.disputeRate")} />
              </Box>
              <SimpleGrid cols={2} spacing="md">
                <SideRow label={t("trustDetail.highValueConcentration")} value={data.highValueReturnsCount} mono />
                <SideRow label={t("insights.trust.cancelled")} value={data.cancelledCount} mono />
              </SimpleGrid>
            </Stack>
            <Text size="sm" c="var(--nw-text-2)" mt="sm">{t("trustDetail.rawMetricsNote")}</Text>
          </DetailCard>

          {/* Recent returns (last 10): reason through i18n (O207) */}
          <DetailCard title={t("trustDetail.recentReturns")}>
            {recentRows.length === 0 ? (
              <Text size="sm" c="var(--nw-text-2)">{t("trustDetail.noReturns")}</Text>
            ) : (
              <TableFrame>
                <Table.ScrollContainer minWidth={560}>
                  <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t("listTable.col.retNumber")}</Table.Th>
                        <Table.Th>{t("filters.reasonLabel")}</Table.Th>
                        <Table.Th>{t("detail.field.date")}</Table.Th>
                        <Table.Th ta="right">{t("listTable.col.amount")}</Table.Th>
                        <Table.Th>{t("listTable.col.status")}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {recentRows.map((r) => (
                        <Table.Tr key={r.returnRequestId}>
                          <Table.Td>
                            <Anchor component={Link} to={`/returns/${r.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no">
                              {r.returnNumber}
                            </Anchor>
                          </Table.Td>
                          <Table.Td><Text size="sm">{reasonLabel(r.returnReason) || "-"}</Text></Table.Td>
                          <Table.Td><Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{dateFormat(r.requestedAt)}</Text></Table.Td>
                          <Table.Td ta="right"><Text size="sm" ff="monospace" fz="var(--nw-fz-mono)">{currencyFormat(r.totalRequestedAmount ?? 0)}</Text></Table.Td>
                          <Table.Td><ReturnStatusBadge status={r.status} /></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </TableFrame>
            )}
          </DetailCard>

          {/* Benchmark widget — tenant-wide comparison */}
          <TrustBenchmarkWidget businessId={businessId} />
        </Stack>
      )}
    </PageLayout>
  );
}
